/**
 * Freeze V2 — Grading Tab
 *
 * Thin wrapper around the shared GradingMatrix component.
 * Bridges GradingMatrix's GradingGridRow[] interface with
 * FreezeFormContext's GradingData record format.
 */
import { memo, useCallback, useMemo } from 'react';

import { GradingMatrix } from '@/components/common/grading-grid/GradingMatrix';
import type { GradingGridRow } from '@/components/common/grading-grid/types';

import { type GradingData, useFreezeForm } from '../../contexts';

const STAGES = [9, 8, 7, 6, 5, 4] as const;
const GRADES = [1, 2, 3] as const;
type StageField = `s${(typeof STAGES)[number]}`;

/** Convert context GradingData → GradingGridRow[] for the matrix. */
function toRows(gd: GradingData): GradingGridRow[] {
  return GRADES.map((grade) => {
    const row = gd[grade] ?? {};
    const s4 = row.s4 ?? null;
    const s5 = row.s5 ?? null;
    const s6 = row.s6 ?? null;
    const s7 = row.s7 ?? null;
    const s8 = row.s8 ?? null;
    const s9 = row.s9 ?? null;
    const total = [s4, s5, s6, s7, s8, s9].reduce(
      (sum: number, v) => sum + (Number(v) || 0),
      0,
    );
    return { grade, s4, s5, s6, s7, s8, s9, total };
  });
}

/** Convert GradingGridRow[] back to GradingData for the context. */
function toGradingData(rows: GradingGridRow[]): GradingData {
  const result: GradingData = {} as GradingData;
  for (const row of rows) {
    result[row.grade] = Object.fromEntries(
      STAGES.map((s) => [`s${s}`, row[`s${s}` as StageField] ?? null]),
    ) as Record<StageField, number | null>;
  }
  return result;
}

export const GradingTab = memo(function GradingTab() {
  const { gradingData, dispatch } = useFreezeForm();

  const rows = useMemo(() => toRows(gradingData), [gradingData]);

  const handleChange = useCallback(
    (updatedRows: GradingGridRow[]) => {
      dispatch({ type: 'SET_GRADING_ROWS', rows: toGradingData(updatedRows) });
    },
    [dispatch],
  );

  const handleClear = useCallback(
    () => dispatch({ type: 'CLEAR_GRADING' }),
    [dispatch],
  );

  return (
    <div className="flex flex-col items-start gap-3">
      <GradingMatrix
        data={rows}
        onChange={handleChange}
        onClear={handleClear}
        title="Grading Matrix"
      />
      <p className="text-muted-foreground max-w-[29rem] text-xs">
        Enter counts per stage × grade, then expand a mating and click{' '}
        <span className="text-foreground font-medium">Add From Grid</span> to
        create those embryos for that mating.
      </p>
    </div>
  );
});
